<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStrapiPost } from 'shared/composables/useStrapiApi';
import { useQuasar } from 'quasar';
import { mdiClose } from '@quasar/extras/mdi-v7';
import useValidators from 'src/shared/composables/useValidators';
import BButton from 'src/shared/components/base/BButton/BButton.vue';
import BInput from 'src/shared/components/base/BInput/BInput.vue';
import { useI18n } from 'vue-i18n';
const { t: $t } = useI18n();

const route = useRoute();
const router = useRouter();
const $q = useQuasar();

const password = ref('');
const passwordConfirmation = ref('');
const isSubmitting = ref(false);

const code = computed(() => (route.query.code as string) || '');

const { required, min } = useValidators();

const { execute, error } = useStrapiPost('auth/reset-password' as never);

async function onSubmit() {
  if (!code.value) {
    $q.notify({ type: 'negative', message: $t('RESET_CODE_MISSING') });
    return;
  }
  isSubmitting.value = true;
  try {
    await execute({
      data: {
        password: password.value,
        passwordConfirmation: passwordConfirmation.value,
        code: code.value,
      },
    });

    if (error.value) {
      const msg = (error.value as { message?: string })?.message;
      $q.notify({
        type: 'negative',
        message: msg || $t('FAILED_TO_RESET_PASSWORD'),
      });
      return;
    }

    $q.notify({
      type: 'positive',
      message: $t('PASSWORD_RESET_SUCCESSFUL'),
    });
    // Back to the main sign-in, not the child login: children have no reachable
    // mailbox and are excluded from password reset server-side, so whoever just
    // reset a password is a parent signing in with credentials.
    await router.push({ name: 'users.signin' });
  } catch (err) {
    console.error($t('RESET_PASSWORD_ERROR'), err);
    $q.notify({
      type: 'negative',
      message: $t('FAILED_TO_RESET_PASSWORD'),
    });
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <q-card
    square
    flat
    class="full-width bg-transparent"
  >
    <q-card-section class="column items-center q-pb-none">
      <!-- White mark: this view only ever renders on the dark SET App page. -->
      <q-img
        src="/img/logo_set_white.svg"
        no-spinner
        alt="SET App"
        class="set-auth-logo"
      />
      <h3 class="text-h4 q-my-md q-px-sm text-center text-uppercase">
        {{ $t('RESET_PASSWORD') }}
      </h3>
    </q-card-section>
    <q-card-section
      class="q-mx-auto full-width"
      style="max-width: 400px"
    >
      <q-form
        class="q-px-sm q-pt-md q-gutter-y-lg"
        @submit.prevent="onSubmit"
      >
        <b-input
          v-model="password"
          clearable
          :clear-icon="mdiClose"
          no-error-icon
          type="password"
          lazy-rules
          :rules="[required, (val: string) => min(val, 6)]"
          :label="$t('PASSWORD')"
          autocomplete="new-password"
        />

        <b-input
          v-model="passwordConfirmation"
          clearable
          :clear-icon="mdiClose"
          no-error-icon
          type="password"
          lazy-rules
          :rules="[
            required,
            (val: string) => val === password || $t('PASSWORDS_MUST_MATCH'),
          ]"
          :label="$t('CONFIRM_NEW_PASSWORD')"
          autocomplete="new-password"
        />

        <b-button
          unelevated
          color="secondary"
          :loading="isSubmitting"
          text-color="dark"
          shadow-color="#FFFFFF"
          class="full-width"
          :label="$t('RESET_PASSWORD')"
          type="submit"
        />
      </q-form>
    </q-card-section>
    <!-- Constrained to the form column: a full-bleed rule runs straight through
         the decorative art in the page corners. -->
    <q-separator class="login-register-card" />
    <q-card-section
      class="row items-center justify-center q-gap-sm q-gutter-md"
    >
      <q-btn
        flat
        size="sm"
        class="q-my-none q-py-none"
        color="accent"
        :to="{ name: 'users.signin' }"
        :label="$t('BACK_TO_LOGIN')"
      />
    </q-card-section>
  </q-card>
</template>

<style scoped>
/* Declared locally rather than leaning on the global rule that happens to live
   in RegisterSignInCard's unscoped style block. */
.login-register-card {
  /* `width` is required, not just `max-width`: the page renders this card as a
     flex column with `items-center`, so a child with no intrinsic content — the
     separator — otherwise shrinks to zero width and disappears. */
  width: 100%;
  max-width: 400px;
  margin: auto;
}

.set-auth-logo {
  width: 11rem;
  max-width: 60vw;
}
</style>
